import { UserMinus } from "lucide-react";
import {
  useDataProvider,
  useGetList,
  useListContext,
  useNotify,
  useRefresh,
  useTranslate,
} from "ra-core";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import type { NevoraDataProvider } from "../datos/dataProvider";
import type { Comercial } from "../dominio/tipos";

/**
 * Acciones en bloque de Leads (auditoría 30/10/2026; solo Jan y Robert): marcar varias fichas y
 * asignarlas a alguien o devolverlas a «Sin asignar». Usa la misma función de la base que la
 * pantalla Sin asignar (`nevora_asignar`, solo administradores).
 */
export const AccionesEnBloque = () => {
  const translate = useTranslate();
  const notify = useNotify();
  const refresh = useRefresh();
  const dataProvider = useDataProvider<NevoraDataProvider>();
  const { selectedIds, onUnselectItems } = useListContext();
  const [destino, setDestino] = useState("");
  const [guardando, setGuardando] = useState(false);
  const { data: usuarios } = useGetList<Comercial>("sales", {
    pagination: { page: 1, perPage: 100 },
    sort: { field: "first_name", order: "ASC" },
  });
  // El consultor no recibe leads (36).
  const personas = (usuarios ?? []).filter(
    (u) => !u.disabled && u.rol_comercial !== "consultor",
  );

  const aplicar = async (a: number | null) => {
    if (!selectedIds.length) return;
    setGuardando(true);
    try {
      const n = await dataProvider.asignar(selectedIds.map(Number), a);
      notify(
        a == null
          ? "nevora.en_bloque.devueltos"
          : "nevora.sin_asignar.asignados",
        { type: "success", messageArgs: { smart_count: n } },
      );
      onUnselectItems();
      setDestino("");
      refresh();
    } catch (e) {
      notify(e instanceof Error ? e.message : "ra.notification.http_error", {
        type: "error",
      });
    } finally {
      setGuardando(false);
    }
  };

  return (
    <div
      className="flex flex-wrap items-center gap-2"
      data-testid="acciones-en-bloque"
    >
      <Select value={destino} onValueChange={setDestino}>
        <SelectTrigger
          className="h-8 w-48"
          aria-label={translate("nevora.sin_asignar.a_quien")}
        >
          <SelectValue placeholder={translate("nevora.sin_asignar.a_quien")} />
        </SelectTrigger>
        <SelectContent>
          {personas.map((u) => (
            <SelectItem key={String(u.id)} value={String(u.id)}>
              {`${u.first_name ?? ""} ${u.last_name ?? ""}`.trim()}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Button
        type="button"
        size="sm"
        disabled={!destino || guardando}
        onClick={() => aplicar(Number(destino))}
      >
        {translate("nevora.sin_asignar.asignar", {
          smart_count: selectedIds.length,
        })}
      </Button>
      <Button
        type="button"
        size="sm"
        variant="outline"
        disabled={guardando}
        onClick={() => aplicar(null)}
      >
        <UserMinus className="size-4" aria-hidden />
        {translate("nevora.en_bloque.quitar_dueno")}
      </Button>
    </div>
  );
};
